import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Field from '../../../util/field/Field';
import Dropdown from '../../../util/dropdown/Dropdown';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';

/**
 * Formulario para cargar y validar PT asignado a artículo inventario
 */

const validationSchema = Yup.object({
    SegmentId: Yup.string().required("Campo Segmentación es requerido.").nullable(),
    UnitPurchase: Yup.string().required("Campo Unidad pedido a sucursal es requerido.").nullable(),
    StorageUnit: Yup.string().required("Campo Unidad almacenamiento externo es requerido.").nullable(),
    UnitSale: Yup.string().required("Campo Unidad para venta es requerido.").nullable(),
    FactorBuyMin: Yup.number()
        .min(.00001, "Ingrese valor mayor a cero.")
        .required("Campo Múltiplos para pedido sucursal es requerido."),
    ConvFactorBuy: Yup.number()
        .min(.00001, "Ingrese valor mayor a cero.")
        .required("Campo Cantidad conversión almacén es requerido."),
    ConvFactorSale: Yup.number()
        .min(.00001, "Ingrese valor mayor a cero.")
        .required("Campo Equivalencia venta es requerido."),
    AmountMax: Yup.number()
        .min(1, "El valor mínimo es 1.")
        .required("Campo Cantidad máxima para solicitar es requerido."),
    AmountMin: Yup.number()
        .min(1, "El valor mínimo es 1.")
        .required("Campo Cantidad Mínima para solicitar es requerido.")
        .test('qyt-match', 'Cantidad mínima debe ser menor a cantidad máxima.', function (value) {
            return this.parent.AmountMax > value;
        }),
    InventoryTypeId: Yup.string().required("Campo Clasificación Inventario es requerido.").nullable(),
    OrderTypeId: Yup.string().required("Campo Clasificación Pedido es requerido.").nullable(),    
});

// Método para encontrar el id de unidad en la lista de unidades
const findUnitId = (list, id, idlist) => {
    if (!list) return null;
    const found = list.find((item) => item[idlist] === id);
    return found ? found[idlist] : null;
};

// Método para crear un input de tipo number con validación y tooltip. Se usa para los campos de cantidad y equivalencias.
const FormNumberField = ({
    label, tooltip, name, values, errors, touched,
    handleChange, handleBlur, placeholder, disabled,
}) => {
    const hasError = !!(errors[name] && touched[name]);
    return (
        <Field
            label={label}
            showTooltip={!!tooltip}
            textTooltip={tooltip}
            hasError={hasError}
            msgError={hasError ? errors[name] : ""}
        >
            <input
                className={`input ${hasError ? "is-danger" : ""}`}
                type="number"
                name={name}
                id={name}
                value={values[name]}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
            />
        </Field>
    );
};

FormNumberField.propTypes = {
    label: PropTypes.string.isRequired,
    tooltip: PropTypes.string,
    name: PropTypes.string.isRequired,
    values: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleBlur: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
};

// Método para crear un dropdown con validación y tooltip. Se usa para los campos de unidades y segmentación.
const FormDropdownField = ({
    label, tooltip, name, items, id, nameAux, labelKey, value, disabled,
    errors, touched, onSelect, onBlur,
}) => {
    const hasError = !!(errors[name] && touched[name]);
    return (
        <Field
            label={label}
            showTooltip={!!tooltip}
            textTooltip={tooltip}
            hasError={hasError}
            msgError={hasError ? errors[name] : ""}
        >
            <Dropdown
                single
                items={items}
                value={value}
                name={nameAux}
                id={id}
                labelKey={labelKey}
                onSelect={onSelect}
                onBlur={onBlur}
                hasError={hasError}
                disabled={disabled}
            />
        </Field>
    );
};

FormDropdownField.propTypes = {
    label: PropTypes.string.isRequired,
    tooltip: PropTypes.string,
    nameAux: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    items: PropTypes.array,
    labelKey: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
};

class InventoryItemUnitsForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            inventoryItem: { ...this.props.inventoryItem },
            isEditing: false,
            isLoading: false
        }
    }

    // Método que se dispara cuando se selecciona un segmento. Carga los catálogos dependientes y setea los valores en el formulario.
    onSegmentChange = (val, setFieldValue, validateForm, values) => {

        this.saveCurrentSegment(values);

        if (val > 0) {
            var selected = this.props.segmentos[val - 1].SegmentId;

            setFieldValue('SegmentId', selected, false);
            this.loadCatalogsBySegment(selected, setFieldValue, validateForm);
        } else {
            setFieldValue('SegmentId', null);
            this.resetDependentFields(setFieldValue, validateForm, null);
        }
    };

    // Método que carga los catálogos dependientes según el segmento seleccionado y setea los valores en el formulario.
    loadCatalogsBySegment = (segmentId, setFieldValue, validateForm) => {
        // Buscamos la relación Artículo-Segmento para obtener los valores de las unidades y equivalencias
        const artInvtSegmentoAux = (this.state.inventoryItem.tblRelArticuloInvSegmento &&
            this.state.inventoryItem.tblRelArticuloInvSegmento.find((item) => item.idSegmento === segmentId)
        ) || null;

        if (artInvtSegmentoAux == null) {
            this.resetDependentFields(setFieldValue, validateForm, segmentId);
            return;
        }

        // Si encontramos la relación, buscamos los ids de las unidades en los catálogos y seteamos los valores en el formulario
        const unitsIdAux = findUnitId(this.props.unitsGP, artInvtSegmentoAux.idUnidadCompra, 'UnitsId');
        const storageUnitIdAux = findUnitId(this.props.unitsGP, artInvtSegmentoAux.idUnidadAlmacenamiento, 'UnitsId');
        const unitSaleIdAux = findUnitId(this.props.units, artInvtSegmentoAux.idUnidadVenta, 'UnitsId');
        const inventoryTypeIdAux = findUnitId(this.props.inventoryTypes, artInvtSegmentoAux.idTipoInventario, 'InventoryTypeId');
        const orderTypeIdAux = findUnitId(this.props.orderTypes, artInvtSegmentoAux.idTipoPedido, 'OrderTypeId');

        setFieldValue('UnitPurchase', unitsIdAux);
        setFieldValue('StorageUnit', storageUnitIdAux);
        setFieldValue('UnitSale', unitSaleIdAux);
        setFieldValue('OrderTypeId', orderTypeIdAux);
        setFieldValue('InventoryTypeId', inventoryTypeIdAux);
        setFieldValue('FactorBuyMin', artInvtSegmentoAux.fcaCompraMin, false);
        setFieldValue('ConvFactorBuy', artInvtSegmentoAux.fcaConvCompra, false);
        setFieldValue('ConvFactorSale', artInvtSegmentoAux.fcaConvVenta, false);
        setFieldValue('AmountMax', artInvtSegmentoAux.CantidadMax, false);
        setFieldValue('AmountMin', artInvtSegmentoAux.CantidadMin, false);
        validateForm();
    }

    // Método que resetea los campos en el formulario.
    resetDependentFields = (setFieldValue, validateForm, segmentId = null) => {
        setFieldValue('UnitPurchase', null, false);
        setFieldValue('StorageUnit', null, false);
        setFieldValue('UnitSale', null, false);
        setFieldValue('FactorBuyMin', 0, false);
        setFieldValue('ConvFactorBuy', 0, false);
        setFieldValue('ConvFactorSale', 0, false);
        setFieldValue('AmountMax', 0, false);
        setFieldValue('AmountMin', 0, false);
        setFieldValue('OrderTypeId', null, false);
        setFieldValue('InventoryTypeId', null, false);
        validateForm();
    }

    // Método que guarda la información del segmento actual en el state del componente.
    saveCurrentSegment = (values, callback) => {
        var segmentId = values.SegmentId;

        if (!segmentId) {
            if (callback) {
                callback();
            }
            return;
        }

        var hasInformation =
            values.InventoryTypeId &&
            values.OrderTypeId &&
            values.UnitPurchase &&
            values.StorageUnit &&
            values.UnitSale &&
            values.FactorBuyMin >= 0.00001 &&
            values.ConvFactorBuy >= 0.00001 &&
            values.ConvFactorSale >= 0.00001 &&
            values.AmountMax >= 1 &&
            values.AmountMin >= 1 &&
            values.AmountMax > values.AmountMin;

        if (!hasInformation) {
            if (callback) {
                callback();
            }
            return;
        }

        this.setState(prevState => {
            var segments =
                prevState.inventoryItem.tblRelArticuloInvSegmento || [];

            var exists = segments.find(
                item => item.idSegmento === segmentId
            );

            var segment = {
                idSegmento: segmentId,
                idTipoPedido: values.OrderTypeId,
                idTipoInventario: values.InventoryTypeId,
                idUnidadCompra: values.UnitPurchase,
                idUnidadAlmacenamiento: values.StorageUnit,
                idUnidadVenta: values.UnitSale,
                fcaCompraMin: values.FactorBuyMin,
                fcaConvCompra: values.ConvFactorBuy,
                fcaConvVenta: values.ConvFactorSale,
                CantidadMax: values.AmountMax,
                CantidadMin: values.AmountMin
            };

            var updatedSegments;

            if (exists) {
                updatedSegments = segments.map(item =>
                    item.idSegmento === segmentId
                        ? { ...item, ...segment }
                        : item
                );
            } else {
                updatedSegments = [
                    ...segments,
                    segment
                ];
            }

            return {
                inventoryItem: {
                    ...prevState.inventoryItem,
                    tblRelArticuloInvSegmento: updatedSegments
                }
            };
        }, callback);
    };
    

    render() {
        const { segmentos, orderTypes, inventoryTypes, unitsGP, units, back, save } = this.props;

        return (
            <Formik
                initialValues={this.state.inventoryItem}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    this.saveCurrentSegment(values, () => {
                        this.props.save(this.state.inventoryItem);
                    });                    
                }}
            >
                {({ values, touched, errors, handleChange, handleBlur, setFieldValue, setFieldTouched, validateForm }) => (
                    <Form id="frmEquivalencias">
                        <div className="row">
                            <header className="card-header card-header-receta is-justify-content-center">
                                <p className="card-header-title is-centered"> Segmentación de Equivalencias </p>
                            </header>
                            <div className="box">
                                {/* SEGMENTOS */}
                                <div className="row">
                                    <FormDropdownField
                                        label="Segmentación"
                                        name="SegmentId"
                                        id="SegmentId"
                                        items={segmentos}
                                        labelKey="Name"
                                        value={values.SegmentId}
                                        errors={errors}
                                        touched={touched}
                                        disabled={false}
                                        onSelect={(val) => this.onSegmentChange(val, setFieldValue, validateForm, values)}
                                        onBlur={() => setFieldTouched('SegmentId', true)}
                                    />
                                </div>

                                <div className="row" style={{ marginTop: '45px' }}>
                                    <div className='columns is-multiline'>
                                        <div className="column is-6">
                                            <FormDropdownField
                                                label="Clasificación Pedido"
                                                tooltip="Clasificación del artículo para el sistema de pedidos."
                                                name="OrderTypeId"
                                                id="OrderTypeId"
                                                items={orderTypes}
                                                labelKey="Description"
                                                value={values.OrderTypeId}
                                                errors={errors}
                                                touched={touched}
                                                disabled={values.SegmentId == null}
                                                onSelect={(val) => {
                                                    const selected = val > 0 ? orderTypes[val - 1].OrderTypeId : null;
                                                    setFieldValue('OrderTypeId', selected);
                                                }}
                                                onBlur={() => setFieldTouched('OrderTypeId', true)}
                                            />
                                        </div>
                                        <div className="column is-6">
                                            <FormDropdownField
                                                label="Clasificación Inventario"
                                                tooltip="Clasificación del artículo para el sistema de inventarios."
                                                name="InventoryTypeId"
                                                id="InventoryTypeId"
                                                items={inventoryTypes}
                                                labelKey="Description"
                                                value={values.InventoryTypeId}
                                                errors={errors}
                                                touched={touched}
                                                disabled={values.SegmentId == null}
                                                onSelect={(val) => {
                                                    const selected = val > 0 ? inventoryTypes[val - 1].InventoryTypeId : null;
                                                    setFieldValue('InventoryTypeId', selected);
                                                }}
                                                onBlur={() => setFieldTouched('InventoryTypeId', true)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row" style={{ marginTop: '25px' }}>
                                    <div className='columns is-multiline'>
                                        <div className="column is-4">
                                            <FormDropdownField
                                                label="Unidad pedido a sucursal"
                                                tooltip="Unidad con la que envían de sucursal a pdv"
                                                name="UnitPurchase"
                                                id="UnitsId"   
                                                items={unitsGP}
                                                labelKey="Name"
                                                value={values.UnitPurchase}
                                                errors={errors}
                                                touched={touched}
                                                disabled={values.SegmentId == null}
                                                onSelect={(val) => {
                                                    const selected = val > 0 ? unitsGP[val - 1].UnitsId : null;
                                                    setFieldValue('UnitPurchase', selected);
                                                }}
                                                onBlur={() => setFieldTouched('UnitPurchase', true)}
                                            />
                                        </div>
                                        <div className="column is-4">
                                            <FormDropdownField
                                                label="Unidad almac. externo PDV"
                                                tooltip="Unidad de almacenamiento pdv"
                                                name="StorageUnit"
                                                id="UnitsId" 
                                                items={unitsGP}
                                                labelKey="Name"
                                                value={values.StorageUnit}
                                                errors={errors}
                                                touched={touched}
                                                disabled={values.SegmentId == null}
                                                onSelect={(val) => {
                                                    const selected = val > 0 ? unitsGP[val - 1].UnitsId : null;
                                                    setFieldValue('StorageUnit', selected);
                                                }}
                                                onBlur={() => setFieldTouched('StorageUnit', true)}
                                            />
                                        </div>
                                        <div className="column is-4">
                                            <FormDropdownField
                                                label="Unidad para venta"
                                                tooltip="Unidad que se vende en pdv"
                                                name="UnitSale"
                                                id="UnitsId"
                                                items={units}
                                                labelKey="Name"
                                                value={values.UnitSale}
                                                errors={errors}
                                                touched={touched}
                                                disabled={values.SegmentId == null}
                                                onSelect={(val) => {
                                                    const selected = val > 0 ? units[val - 1].UnitsId : null;
                                                    setFieldValue('UnitSale', selected);
                                                }}
                                                onBlur={() => setFieldTouched('UnitSale', true)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row" style={{ marginTop: '25px' }}>
                                    <div className='columns is-multiline'>
                                        <div className="column is-4">
                                            <FormNumberField
                                                label="Múltiplos pdo. sucursal"
                                                tooltip="Múltiplos para pedido sucursal"
                                                name="FactorBuyMin"
                                                values={values}
                                                errors={errors}
                                                touched={touched}
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                                placeholder="Múltiplos de pedido a sucursal"
                                                disabled={values.SegmentId == null}
                                            />
                                        </div>
                                        <div className="column is-4">
                                            <FormNumberField
                                                label="Equivalencia almacén"
                                                tooltip="Cantidad conversión almacén"
                                                name="ConvFactorBuy"
                                                values={values}
                                                errors={errors}
                                                touched={touched}
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                                placeholder="Cantidad de conversión almacén"
                                                disabled={values.SegmentId == null}
                                            />
                                        </div>
                                        <div className="column is-4">
                                            <FormNumberField
                                                label="Equivalencia venta"
                                                tooltip="Cantidad conversión venta"
                                                name="ConvFactorSale"
                                                values={values}
                                                errors={errors}
                                                touched={touched}
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                                placeholder="Cantidad de conversión a la venta"
                                                disabled={values.SegmentId == null}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row" style={{ marginTop: '25px' }}>
                                    <div className='columns is-multiline'>
                                        <div className="column is-4">
                                            <FormNumberField
                                                label="Cantidad máx. a solicitar"
                                                tooltip="Cantidad máxima que le puedo pedir a sucursal"
                                                name="AmountMax"
                                                values={values}
                                                errors={errors}
                                                touched={touched}
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                                placeholder="Ingrese cantidad máxima a solicitar"
                                                disabled={values.SegmentId == null}
                                            />
                                        </div>
                                        <div className="column is-4">
                                            <FormNumberField
                                                label="Cantidad Mínima para solicitar"
                                                tooltip="Cantidad mínima que le puedo pedir a sucursal"
                                                name="AmountMin"
                                                values={values}
                                                errors={errors}
                                                touched={touched}
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                                placeholder="Cantidad mínima a solicitar"
                                                disabled={values.SegmentId == null}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <br />
                        <div className="has-text-left buttons-modal" id="stepActions">
                            <div className="pull-left">
                                <button id="btnBackStepUnits" type="button" className="button is-light" onClick={back}>Anterior</button>
                            </div>
                        </div>
                        <div className="has-text-right buttons-modal" id="stepActions">
                            <div className="pull-right">
                                <button id="btnContinuarStepUnits" type="submit" className="button is-success">Continuar</button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        )
    }
}

InventoryItemUnitsForm.propTypes = {
    isEditing: PropTypes.bool,
    inventoryItem: PropTypes.object,
    segmentos: PropTypes.array,
    orderTypes: PropTypes.array,
    inventoryTypes: PropTypes.array,
    unitsGP: PropTypes.array,
    units: PropTypes.array,
    save: PropTypes.func.isRequired,
    back: PropTypes.func.isRequired,
};

// Definición de los valores por default de las props en caso de no ser recibidas por el padre.
InventoryItemUnitsForm.defaultProps = {
    isEditing: false,
    inventoryItem: {
        CodeProvider: "",
        InventoryCode: "",
        Name: "",
        ShortName: "",
        CodeSat: null,
        IsTopping: false,
        ToppingPrice: 0,
        ToppingWeight: 0,
        tblRelArticuloInvSegmento: []
    }
}

export default InventoryItemUnitsForm;